/**
 * Firebase Cloud Messaging — web push token registration (after login).
 * Stores tokens on users/{uid}.fcmToken and users/{uid}/devices/{deviceId}.
 */
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { app, db } from '../firebase';
import { perfTimeline } from '../utils/startupPerf';
import { getServiceWorkerRegistration } from './serviceWorkerRegistration';

const DEVICE_ID_KEY = 'lockedin_device_id';

let messagingInstance = null;

function getVapidKey() {
  return process.env.REACT_APP_FCM_VAPID_KEY || '';
}

function getOrCreateDeviceId() {
  if (typeof window === 'undefined') return 'web';
  try {
    let id = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = typeof crypto?.randomUUID === 'function'
        ? crypto.randomUUID()
        : `web_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch (_) {
    return `web_${Date.now()}`;
  }
}

async function getMessagingInstance() {
  if (messagingInstance) return messagingInstance;
  const supported = await isSupported();
  if (!supported) return null;
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

export async function saveFcmToken(uid, token) {
  if (!uid || !token) return;
  const deviceId = getOrCreateDeviceId();
  const now = serverTimestamp();

  await Promise.all([
    setDoc(
      doc(db, 'users', uid),
      { fcmToken: token, fcmTokenUpdatedAt: now },
      { merge: true }
    ),
    setDoc(
      doc(db, 'users', uid, 'devices', deviceId),
      {
        fcmToken: token,
        platform: 'web',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent?.slice(0, 200) : null,
        updatedAt: now,
      },
      { merge: true }
    ),
  ]);

  perfTimeline('pwa.fcmTokenSaved', 'pwa', { uid, deviceId });
}

export async function registerFcmToken(uid) {
  if (!uid || typeof window === 'undefined') return null;

  const vapidKey = getVapidKey();
  if (!vapidKey) {
    console.warn('[FCM] REACT_APP_FCM_VAPID_KEY not set — push disabled');
    return null;
  }

  const messaging = await getMessagingInstance();
  if (!messaging) return null;

  const registration = await getServiceWorkerRegistration();
  if (!registration) return null;

  try {
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    if (token) {
      await saveFcmToken(uid, token);
      perfTimeline('pwa.fcmTokenRegistered', 'pwa');
    }
    return token;
  } catch (err) {
    console.warn('registerFcmToken:', err);
    return null;
  }
}

export async function requestNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return false;
  }
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  perfTimeline('pwa.notificationPermission', 'pwa', { result });
  return result === 'granted';
}

export function subscribeForegroundMessages(onPayload) {
  return getMessagingInstance().then((messaging) => {
    if (!messaging) return () => {};
    return onMessage(messaging, (payload) => {
      perfTimeline('pwa.foregroundPush', 'pwa', {
        type: payload?.data?.type,
      });
      onPayload?.(payload);
    });
  });
}
